import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Settings, ChevronDown, RotateCcw, LogOut } from "lucide-react";
// Real prod nav leaves (zero-coupling): the bar chrome + active-aware link.
import { NavShell } from "@/components/layout/NavShell";
import { NavLink } from "@/components/layout/NavLink";
import { DEMO_THERAPIST } from "./mock-data";
import { useDemoState } from "./DemoStateProvider";

/**
 * DemoAppNav — the persistent authed app nav, mirroring the real Psy180 dashboard
 * bar. Reuses the real NavShell (Ψ + Psy180° lockup, blur bar) + NavLink (gold
 * active glow), so it's pixel-identical to the product. The 6 page tabs route to
 * the demo's tabs; the account chip + Sign out reset the in-memory demo.
 */
export function DemoAppNav() {
  const navigate = useNavigate();
  const { reset } = useDemoState();
  const [menuOpen, setMenuOpen] = useState(false);

  const restart = () => { reset(); setMenuOpen(false); navigate("/dashboard"); };

  return (
    <NavShell position="sticky" contained={false}>
      {/* min-w-0 lets this flex child shrink below its content width; below xl the
          tabs scroll horizontally instead of forcing the whole page wide (mobile fix).
          The ml-auto spacer right-aligns when there's room — unlike justify-end it
          doesn't break leftward scrollability once the row overflows. */}
      <nav className="flex min-w-0 flex-1 items-center gap-1 max-xl:overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [&>*]:shrink-0">
        <div aria-hidden className="ml-auto" />
        {/* Profile lives under the Settings gear now (Settings is the account
            hub — Profile is its top section), mirroring the real app. */}
        <NavLink href="/session">Session</NavLink>
        <NavLink href="/clients">Clients</NavLink>
        <NavLink href="/calendar">Calendar</NavLink>
        <NavLink href="/soundboard">Soundboard</NavLink>
        <NavLink href="/history">History</NavLink>
        <NavLink href="/scripts">Scripts</NavLink>

        {/* Account chip (the demo's stand-in for the client/account switcher) */}
        <div className="relative ml-2">
          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            className="inline-flex items-center gap-1.5 rounded-md border border-[var(--color-border)] px-3 py-2 font-mono text-[12px] uppercase tracking-[0.12em] text-[var(--color-gold-soft)] transition-colors hover:border-[var(--color-gold)]"
          >
            {DEMO_THERAPIST.name.split(",")[0]} <ChevronDown className="h-3 w-3" />
          </button>
          {/* Below xl the nav row is a scroll container which would clip an
              absolute dropdown — switch to fixed so the menu escapes the clip.
              (NavShell's backdrop-blur makes it the containing block, so these
              coords resolve against the sticky header — visually identical.) */}
          {menuOpen && (
            <div className="absolute right-0 top-full mt-1.5 w-52 rounded-lg border border-[var(--color-border-gold)] bg-[var(--color-bg-deep)] p-1.5 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.7)] max-xl:fixed max-xl:right-3 max-xl:top-[54px] max-xl:mt-0">
              <p className="px-2.5 py-1.5 font-mono text-[9px] uppercase tracking-[0.16em] text-[var(--color-text-ghost)]">Demo account</p>
              <p className="px-2.5 pb-2 text-[13px] text-[var(--color-text-muted)]">{DEMO_THERAPIST.name}</p>
              <button type="button" onClick={restart} className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left font-mono text-[11px] uppercase tracking-wide text-[var(--color-text-primary)] transition-colors hover:bg-[var(--color-navy-mid)]">
                <RotateCcw className="h-3.5 w-3.5" /> Restart demo
              </button>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-1.5 rounded-md border border-[var(--color-border)] px-3 py-2 font-mono text-[12px] uppercase tracking-[0.12em] text-[var(--color-text-muted)] transition-colors hover:border-[var(--color-gold)] hover:text-[var(--color-gold-soft)]"
        >
          <LogOut className="h-3.5 w-3.5" /> Sign out
        </button>

        <button
          type="button"
          onClick={() => navigate("/settings")}
          className="inline-flex items-center gap-1.5 rounded-md border border-[var(--color-border-gold)] px-3 py-2 font-mono text-[12px] uppercase tracking-[0.12em] text-[var(--color-gold-soft)] transition-colors hover:bg-[var(--color-navy-light)]"
        >
          <Settings className="h-3.5 w-3.5" /> Settings
        </button>
      </nav>
    </NavShell>
  );
}
